export interface CEPData {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
}

export interface CEPError {
  erro: boolean;
}

export class CEPApiUnavailableError extends Error {
  constructor(message: string = 'API do ViaCEP está indisponível') {
    super(message);
    this.name = 'CEPApiUnavailableError';
  }
}

class CEPService {
  private readonly baseURL = 'https://viacep.com.br/ws';
  private readonly timeout = 10000; // 10 segundos

  /**
   * Busca dados do CEP na API ViaCEP
   * @param cep - CEP no formato 00000-000 ou 00000000
   * @returns Promise com os dados do CEP ou erro
   */
  async buscarCEP(cep: string): Promise<CEPData> {
    try {
      // Remove caracteres não numéricos
      const cepLimpo = cep.replace(/\D/g, '');
      
      // Valida se o CEP tem 8 dígitos
      if (cepLimpo.length !== 8) {
        throw new Error('CEP deve ter 8 dígitos');
      }

      // Cria um AbortController para timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      try {
        const response = await fetch(`${this.baseURL}/${cepLimpo}/json/`, {
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          // Se a resposta não está ok, pode ser que a API esteja fora do ar
          throw new CEPApiUnavailableError();
        }

        const data: CEPData | CEPError = await response.json();

        // Verifica se o CEP foi encontrado
        if ('erro' in data && data.erro) {
          throw new Error('CEP não encontrado');
        }

        return data as CEPData;
      } catch (fetchError: unknown) {
        clearTimeout(timeoutId);
        const err = fetchError as { name?: string; message?: string };

        // Se foi abortado por timeout ou erro de rede, a API está indisponível
        if (err.name === 'AbortError' ||
            fetchError instanceof TypeError ||
            err.message?.includes('fetch') ||
            err.message?.includes('network') ||
            err.message?.includes('Failed to fetch')) {
          throw new CEPApiUnavailableError();
        }
        
        // Re-lança outros erros
        throw fetchError;
      }
    } catch (error) {
      // Re-lança o erro para que o handler possa tratá-lo
      throw error;
    }
  }

  /**
   * Formata CEP para exibição (00000-000)
   * @param cep - CEP no formato 00000000
   * @returns CEP formatado
   */
  formatarCEP(cep: string): string {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      return `${cepLimpo.slice(0, 5)}-${cepLimpo.slice(5)}`;
    }
    return cep;
  }

  /**
   * Valida formato do CEP
   * @param cep - CEP a ser validado
   * @returns true se válido, false caso contrário
   */
  validarCEP(cep: string): boolean {
    const cepLimpo = cep.replace(/\D/g, '');
    return cepLimpo.length === 8;
  }
}

export const cepService = new CEPService();
